import { useState } from 'react';
import {
  Dialog as MuiDialog,
  DialogContent,
  Stepper,
  Step,
  StepLabel,
  Box,
  IconButton,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { HCWCategory } from '@/types/hcw';
import { IndicationType, HandHygieneAction, Opportunity } from '@/types/opportunity';
import type {
  HealthworkerIdentifier,
  HealthworkerColor,
} from '@config/healthworker-stars';
import { getStarByColor } from '@config/healthworker-stars';
import { useLocalizedField } from '@utils/i18n-helpers';
import { HCWCategorySelector } from '../HCWCategorySelector';
import { IndicationSelector } from './IndicationSelector';
import { ActionSelector } from './ActionSelector';

interface OpportunityRecorderProps {
  open: boolean;
  onClose: () => void;
  onSave: (opportunity: Omit<Opportunity, 'opportunityId' | 'timestamp'>) => Promise<void>;
  sessionId: string;
  recentCounts?: Record<string, number>;
  nextOpportunityNumber: number;
  nextSequenceOrder: number;
}

type RecorderStep = 'hcw' | 'indication' | 'action';

export function OpportunityRecorder({
  open,
  onClose,
  onSave,
  sessionId,
  recentCounts,
  nextOpportunityNumber,
  nextSequenceOrder,
}: OpportunityRecorderProps) {
  const { t } = useTranslation();
  const getField = useLocalizedField();
  const [currentStep, setCurrentStep] = useState<RecorderStep>('hcw');
  const [selectedHCW, setSelectedHCW] = useState<HCWCategory | null>(null);
  const [selectedStarId, setSelectedStarId] = useState<HealthworkerIdentifier | null>(null);
  const [selectedStarColor, setSelectedStarColor] = useState<HealthworkerColor | null>(null);
  const [selectedIndications, setSelectedIndications] = useState<IndicationType[]>([]);

  const handleHCWSelect = (
    category: HCWCategory,
    starId: HealthworkerIdentifier,
    starColor: HealthworkerColor
  ) => {
    setSelectedHCW(category);
    setSelectedStarId(starId);
    setSelectedStarColor(starColor);
    setCurrentStep('indication');
  };

  const handleIndicationsSelect = (indications: IndicationType[]) => {
    setSelectedIndications(indications);
    setCurrentStep('action');
  };

  const handleActionComplete = async (action: HandHygieneAction) => {
    if (!selectedHCW || !selectedStarId || !selectedStarColor) return;

    const opportunity: Omit<Opportunity, 'opportunityId' | 'timestamp'> = {
      sessionId,
      observedHCWCode: selectedHCW.code,
      observedHCWName: getField(selectedHCW, 'name'),
      healthworkerIdentifier: selectedStarId,
      healthworkerColor: selectedStarColor,
      opportunityNumber: nextOpportunityNumber,
      sequenceOrder: nextSequenceOrder,
      indications: selectedIndications,
      action,
    };

    await onSave(opportunity);
    handleClose();
  };

  const handleBack = () => {
    if (currentStep === 'action') {
      setCurrentStep('indication');
    } else if (currentStep === 'indication') {
      setCurrentStep('hcw');
    }
  };

  const handleClose = () => {
    setCurrentStep('hcw');
    setSelectedHCW(null);
    setSelectedStarId(null);
    setSelectedStarColor(null);
    setSelectedIndications([]);
    onClose();
  };

  // Get the active hex color for theming downstream steps
  const activeStarHex = selectedStarColor
    ? getStarByColor(selectedStarColor).color
    : undefined;

  const steps = [t('recorder.step1'), t('recorder.step2'), t('recorder.step3')];
  const activeStep =
    currentStep === 'hcw' ? 0 : currentStep === 'indication' ? 1 : 2;

  return (
    <MuiDialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      fullScreen
    >
      <Box sx={{ display: 'flex', alignItems: 'center', px: 1, pt: 1 }}>
        <Stepper activeStep={activeStep} sx={{ flexGrow: 1 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
        <IconButton aria-label="close" onClick={handleClose}>
          <Close />
        </IconButton>
      </Box>

      <DialogContent>
        {currentStep === 'hcw' && (
          <HCWCategorySelector
            onSelect={handleHCWSelect}
            recentCounts={recentCounts}
          />
        )}
        {currentStep === 'indication' && (
          <IndicationSelector
            onNext={handleIndicationsSelect}
            onBack={handleBack}
            initialValue={selectedIndications}
            healthworkerColor={activeStarHex}
          />
        )}
        {currentStep === 'action' && (
          <ActionSelector
            onComplete={handleActionComplete}
            onBack={handleBack}
            healthworkerColor={activeStarHex}
          />
        )}
      </DialogContent>
    </MuiDialog>
  );
}
